import { CheckCircle, Shield, BadgeCheck, Video, FileCheck } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

/**
 * VerifiedBadge - Shows verification status badges for sellers
 * Supports both legacy (GSTIN/IEC) and new (Video ID) verification methods
 */
export function VerifiedBadge({ 
  // New global verification props
  isVerified = false,
  verificationMethod = null, // 'video_id', 'document', 'manual', or null for legacy
  verifiedAt = null,
  
  // Legacy India verification props (backwards compatibility)
  phoneVerified = false, 
  gstinVerified = false, 
  iecStatus = 'not_submitted',
  
  size = 'default', // 'small', 'default', 'large'
  showTooltip = true,
  className = ''
}) {
  // Check if verified via new method OR legacy method
  const isLegacyVerified = phoneVerified && gstinVerified && iecStatus === 'approved';
  const isFullyVerified = isVerified || isLegacyVerified;
  
  if (!isFullyVerified) return null;
  
  // Size classes
  const sizeClasses = {
    small: 'h-4 w-4',
    default: 'h-5 w-5',
    large: 'h-6 w-6'
  };
  
  const badgeSize = sizeClasses[size] || sizeClasses.default;
  
  // Determine badge color based on verification method
  const getBadgeColor = () => {
    if (verificationMethod === 'video_id') return 'bg-green-500 fill-green-500';
    return 'bg-blue-500 fill-blue-500';
  };
  
  const badge = (
    <span 
      className={`inline-flex items-center justify-center ${className}`}
      data-testid="verified-badge"
    >
      <span className="relative inline-flex">
        <span className={`absolute inset-0 ${verificationMethod === 'video_id' ? 'bg-green-500' : 'bg-blue-500'} rounded-full`}></span>
        <BadgeCheck 
          className={`${badgeSize} text-white relative z-10 ${getBadgeColor()}`}
          strokeWidth={2.5}
        />
      </span>
    </span>
  );
  
  if (!showTooltip) return badge;
  
  const getTooltipContent = () => {
    if (verificationMethod === 'video_id') {
      return (
        <div className="text-sm">
          <p className="font-semibold text-green-600 mb-1 flex items-center gap-1">
            <Video className="h-3.5 w-3.5" />
            Video Verified Seller
          </p>
          <ul className="text-xs text-muted-foreground space-y-0.5">
            <li className="flex items-center gap-1">
              <CheckCircle className="h-3 w-3 text-green-500" />
              Identity Verified via Video
            </li>
            <li className="flex items-center gap-1">
              <CheckCircle className="h-3 w-3 text-green-500" />
              Business ID Confirmed
            </li>
            <li className="flex items-center gap-1">
              <CheckCircle className="h-3 w-3 text-green-500" />
              AI + Human Review Passed
            </li>
          </ul>
          {verifiedAt && (
            <p className="text-xs text-muted-foreground mt-1">
              Verified on {new Date(verifiedAt).toLocaleDateString()}
            </p>
          )}
        </div>
      );
    }
    
    // Legacy or document verification
    return (
      <div className="text-sm">
        <p className="font-semibold text-blue-600 mb-1 flex items-center gap-1">
          <Shield className="h-3.5 w-3.5" />
          Verified Seller
        </p>
        <ul className="text-xs text-muted-foreground space-y-0.5">
          {phoneVerified && (
            <li className="flex items-center gap-1">
              <CheckCircle className="h-3 w-3 text-green-500" />
              Phone Number Verified
            </li>
          )}
          {gstinVerified && (
            <li className="flex items-center gap-1">
              <CheckCircle className="h-3 w-3 text-green-500" />
              GSTIN Verified
            </li>
          )}
          {iecStatus === 'approved' && (
            <li className="flex items-center gap-1">
              <CheckCircle className="h-3 w-3 text-green-500" />
              IEC Code Approved
            </li>
          )}
          {verificationMethod === 'document' && (
            <li className="flex items-center gap-1">
              <CheckCircle className="h-3 w-3 text-green-500" />
              Business Documents Verified
            </li>
          )}
        </ul>
      </div>
    );
  };
  
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          {badge}
        </TooltipTrigger>
        <TooltipContent side="top" className="max-w-xs">
          {getTooltipContent()}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

/**
 * Compact verified indicator for inline use
 */
export function VerifiedIndicator({ 
  isVerified = false,
  verificationMethod = null,
  phoneVerified = false, 
  gstinVerified = false, 
  iecStatus = 'not_submitted'
}) {
  const isLegacyVerified = phoneVerified && gstinVerified && iecStatus === 'approved';
  const isFullyVerified = isVerified || isLegacyVerified;
  
  if (!isFullyVerified) return null;
  
  const iconColor = verificationMethod === 'video_id' 
    ? 'text-green-500 fill-green-500' 
    : 'text-blue-500 fill-blue-500';
  
  return (
    <BadgeCheck 
      className={`h-4 w-4 ${iconColor} inline-flex flex-shrink-0`}
      data-testid="verified-indicator"
    />
  );
}

/**
 * VerificationStatus - Shows current verification status with more detail
 */
export function VerificationStatus({ 
  status, // 'not_submitted', 'pending_review', 'approved', 'rejected'
  aiMatchPercentage,
  reviewNotes
}) {
  const statusConfig = {
    not_submitted: {
      icon: Shield,
      color: 'text-slate-500 bg-slate-100',
      label: 'Not Verified',
      description: 'Complete video ID verification to get verified'
    },
    pending_review: {
      icon: Video,
      color: 'text-yellow-600 bg-yellow-100',
      label: 'Under Review',
      description: 'Your verification is being reviewed by our team'
    },
    approved: {
      icon: CheckCircle,
      color: 'text-green-600 bg-green-100',
      label: 'Verified',
      description: 'Your identity has been verified'
    },
    rejected: {
      icon: Shield,
      color: 'text-red-600 bg-red-100',
      label: 'Verification Failed',
      description: reviewNotes || 'Please submit a new verification video'
    }
  };

  const config = statusConfig[status] || statusConfig.not_submitted;
  const Icon = config.icon;

  return (
    <div className={`rounded-lg p-4 ${config.color}`}>
      <div className="flex items-start gap-3">
        <Icon className="h-5 w-5 mt-0.5" />
        <div className="flex-1">
          <p className="font-semibold">{config.label}</p>
          <p className="text-sm opacity-80">{config.description}</p>
          {aiMatchPercentage !== undefined && status === 'pending_review' && (
            <p className="text-sm mt-1">AI Match Score: {aiMatchPercentage}%</p>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * SmallVerifiedBadge - Compact badge for product cards and lists
 */
export function SmallVerifiedBadge({ isVerified, verificationMethod }) {
  if (!isVerified) return null;

  const bgColor = verificationMethod === 'video_id' ? 'bg-green-600' : 'bg-blue-600';

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <span className={`inline-flex items-center justify-center w-5 h-5 rounded-full ${bgColor} text-white`}>
            <CheckCircle className="h-3 w-3" />
          </span>
        </TooltipTrigger>
        <TooltipContent>
          <p className="text-sm">
            {verificationMethod === 'video_id' ? 'Video Verified Seller' : 'Verified Seller'}
          </p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

export default VerifiedBadge;
